import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Zap, Bot, Users, Globe, CheckCircle, ArrowRight, BarChart3, Eye, TrendingUp, BrainCircuit, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const Comparison = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');
  
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
    window.location.href = homeUrl;
  };

  return (
    <Layout>
      <SEOHead 
        title="NoQ5 vs Gong vs Clari vs HubSpot Breeze | Best AI Revenue Platform 2026"
        description="Compare NoQ5 with Gong, Clari, HubSpot Breeze, and Salesforce Einstein. Autonomous CRM updates, evidence-based forecasting, pipeline intelligence. GDPR-compliant."
        canonicalUrl="https://noq5.ai/comparison"
        keywords="NoQ5 vs Gong, NoQ5 vs Clari, AI revenue intelligence comparison, best AI CRM tool, HubSpot AI alternative, Salesforce AI alternative, agentic AI sales comparison, pipeline intelligence comparison"
      />
      <div className="container px-4 md:px-6 py-8 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-block rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary mb-6"
            >
              Revenue Intelligence Comparison
            </motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
              Why CROs Choose NoQ5 Over Native AI
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Your board wants accurate forecasts. Your managers need pipeline visibility. Your reps hate admin work. See how NoQ5 solves all three, where native tools fall short.
            </p>
          </div>

          {/* Comparison Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {/* HubSpot Breeze Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-2xl p-8 text-center hover:shadow-lg transition-shadow"
            >
              <div className="w-16 h-16 rounded-2xl bg-[hsl(24,95%,53%)]/10 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[hsl(24,95%,53%)]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-foreground">HubSpot Breeze</h3>
              <p className="text-muted-foreground mb-6">HubSpot's native AI assistant</p>
              
              <div className="text-left space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">Native HubSpot integration</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">Good for marketing & content generation</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">No evidence-based forecasting</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">No autonomous CRM updates from meetings</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">No pipeline intelligence for leadership</span>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground">
                <strong>Best for:</strong> Marketing teams needing content & email AI
              </p>
            </motion.div>

            {/* NoQ5 Card - Featured */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="relative bg-gradient-to-br from-primary/5 via-card to-primary/5 border-2 border-primary rounded-2xl p-8 text-center shadow-lg"
            >
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1.5 rounded-full text-sm font-semibold">
                Built for Revenue Leaders
              </div>
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-foreground">NoQ5.ai</h3>
              <p className="text-muted-foreground mb-6">AI Revenue Intelligence Platform for HubSpot</p>
              
              <div className="text-left space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">Evidence-based forecasts (replace gut feel)</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">Zero-touch CRM. reps save 8+ hours/week</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">Pipeline visibility & deal health signals</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">AI coaching for every rep, every meeting</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-foreground">GDPR-compliant, EU data residency</span>
                </div>
              </div>
              
              <p className="text-sm text-foreground">
                <strong>Best for:</strong> CROs & VP Sales who need forecast accuracy, pipeline visibility, and rep productivity
              </p>
            </motion.div>
          </div>

          {/* Feature Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card border border-border rounded-2xl overflow-hidden mb-16"
          >
            <div className="bg-primary px-8 py-6">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-primary-foreground">What Matters to Revenue Leaders</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left py-4 px-6 font-semibold text-foreground">Capability</th>
                    <th className="text-center py-4 px-6 font-semibold text-foreground">HubSpot Breeze</th>
                    <th className="text-center py-4 px-6 font-semibold text-primary">NoQ5.ai</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { feature: 'Forecast Accuracy', breeze: { text: 'Manual pipeline reports', good: false }, noq5: { text: 'AI-scored deal confidence with evidence', good: true } },
                    { feature: 'Pipeline Visibility', breeze: { text: 'Standard dashboards', good: false }, noq5: { text: 'Real-time deal health signals & risk alerts', good: true } },
                    { feature: 'Rep Productivity (CRM)', breeze: { text: 'Reps still do manual entry', good: false }, noq5: { text: 'Zero-touch, meetings auto-update CRM', good: true } },
                    { feature: 'Coaching', breeze: { text: 'No built-in coaching', good: false }, noq5: { text: 'AI coach before, during & after meetings', good: true } },
                    { feature: 'Morning Briefing', breeze: { text: 'Not available', good: false }, noq5: { text: 'Daily digest of at-risk deals & actions', good: true } },
                    { feature: 'Competitive Intel', breeze: { text: 'Not available', good: false }, noq5: { text: 'Aggregated across all deals automatically', good: true } },
                    { feature: 'GDPR / EU Data', breeze: { text: 'US-based', warn: true }, noq5: { text: 'Ireland-based, automatic anonymization', good: true } },
                    { feature: 'Setup Time', breeze: { text: 'Built into HubSpot', good: true }, noq5: { text: '5-minute OAuth connection', good: true } },
                    { feature: 'Content Generation', breeze: { text: 'Strong email & marketing AI', good: true }, noq5: { text: 'Post-meeting follow-up drafts', good: true } },
                  ].map((row, i) => (
                    <tr key={i} className={`border-b border-border ${i % 2 === 1 ? 'bg-muted/30' : ''}`}>
                      <td className="py-4 px-6 font-medium text-foreground">{row.feature}</td>
                      <td className={`py-4 px-6 text-center ${row.breeze.good ? 'text-green-600' : row.breeze.warn ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {row.breeze.text}
                      </td>
                      <td className="py-4 px-6 text-center text-primary font-semibold">{row.noq5.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Value for each persona */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-card border border-border rounded-2xl p-8"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-3 text-foreground">For CROs & VP Sales</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Evidence-based forecasts replace gut-feel pipeline reviews</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>At-risk deals surfaced before they surprise you</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Morning briefing with exactly what needs your attention</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="bg-card border border-border rounded-2xl p-8"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <BrainCircuit className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-3 text-foreground">For Sales Managers</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>AI coaching insights per rep based on win/loss patterns</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Deal inspection with every signal and stakeholder interaction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Action items you can assign and track to resolution</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-card border border-border rounded-2xl p-8"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-3 text-foreground">For Sales Reps</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Zero CRM admin, meetings auto-update HubSpot</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>AI meeting prep with talking points & stakeholder maps</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Auto follow-ups drafted after every meeting</span>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Editorial cross-link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-2xl p-6 md:p-8 mb-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <p className="text-sm font-medium text-primary mb-1">Buyer's Guide</p>
              <h3 className="text-xl md:text-2xl font-bold text-foreground">Best AI Sales Assistants in 2026</h3>
              <p className="text-muted-foreground mt-1">Honest comparison of 8 tools, with ROI math and execution-vs-copilot framing.</p>
            </div>
            <a
              href="/best-ai-sales-assistants-2026"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline whitespace-nowrap"
            >
              Read the guide <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-primary rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">See the Difference for Yourself</h3>
            <p className="mb-8 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              Book a demo and see how NoQ5 gives you forecast accuracy, pipeline visibility, and 8+ hours back per rep every week.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={scrollToDemo}
                size="lg"
                className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-xl"
              >
                Book a Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                className="bg-white/20 border-2 border-white text-white hover:bg-white hover:text-primary font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300"
                onClick={scrollToDemo}
              >
                Contact Us
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Comparison;
